import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, FileBarChart, BrainCircuit, TrendingUp, Layers, Zap } from 'lucide-react';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import { useT } from '../i18n';

/* Data & Analytics — Snowflake-inspired "one source of truth" page,
   adapted to restaurants & hospitality, with living charts. */

function Tick({ base, step = 1, every = 2400, prefix = '', suffix = '' }) {
  const [n, setN] = useState(base);
  useEffect(() => {
    const t = setInterval(() => setN((v) => v + Math.ceil(Math.random() * step)), every);
    return () => clearInterval(t);
  }, [step, every]);
  return <>{prefix}{n.toLocaleString()}{suffix}</>;
}

const BARS = [42, 58, 50, 72, 64, 88, 79];

function LiveChart({ isEs }) {
  return (
    <div className="relative rounded-3xl bg-white/[0.05] border border-white/10 p-6 sm:p-7 backdrop-blur">
      <div className="flex items-center justify-between">
        <p className="text-[10.5px] font-bold tracking-[0.18em] uppercase text-neutral-400">{isEs ? 'Ventas · 7 días' : 'Sales · 7 days'}</p>
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
        </span>
      </div>
      <div className="mt-5 flex items-end gap-2 h-36">
        {BARS.map((h, i) => (
          <motion.div
            key={i}
            className={`flex-1 rounded-t-lg ${i === BARS.length - 1 ? 'bg-[#F77F11]' : 'bg-white/15'}`}
            animate={{ height: [`${h}%`, `${Math.min(h + 12, 96)}%`, `${h}%`] }}
            transition={{ duration: 3.2 + i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3">
          <p className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold">{isEs ? 'Ingresos hoy' : 'Revenue today'}</p>
          <p className="mt-1 text-[17px] font-bold text-white">$<Tick base={4218} step={14} every={2100} /></p>
        </div>
        <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3">
          <p className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold">{isEs ? 'Pedidos' : 'Orders'}</p>
          <p className="mt-1 text-[17px] font-bold text-white"><Tick base={132} step={1} every={3400} /></p>
        </div>
        <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3">
          <p className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold">{isEs ? 'Margen' : 'Margin'}</p>
          <p className="mt-1 text-[17px] font-bold text-emerald-400">68%</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-400/10 border border-emerald-400/20 px-3 py-2">
        <Zap className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <p className="text-[11px] text-neutral-300 min-w-0 truncate">
          {isEs ? 'Insight: el brunch del sábado supera al viernes por 22%.' : 'Insight: Saturday brunch outperforms Friday by 22%.'}
        </p>
      </div>
    </div>
  );
}

export default function DataAnalyticsPage() {
  const { lang } = useT();
  const isEs = lang === 'es';

  const capabilities = [
    {
      icon: BarChart3, href: '/services/dashboards',
      t: isEs ? 'Dashboards en vivo' : 'Live Dashboards',
      b: isEs ? 'Ventas, pedidos, delivery y reseñas de todas tus plataformas en una sola pantalla — actualizada sola.' : 'Sales, orders, delivery and reviews from every platform on one screen — updated automatically.',
    },
    {
      icon: FileBarChart, href: '/services/performance-reporting',
      t: isEs ? 'Reportes de Rendimiento' : 'Performance Reporting',
      b: isEs ? 'Un reporte ejecutivo mensual que no termina en datos: termina en una acción recomendada.' : 'A monthly executive report that doesn\'t end in data — it ends in a recommended action.',
    },
    {
      icon: BrainCircuit, href: '/services/business-intelligence',
      t: 'Business Intelligence',
      b: isEs ? 'La IA detecta patrones que un humano no ve: platos que pierden margen, horas muertas, clientes que no vuelven.' : 'AI finds patterns humans miss: dishes losing margin, dead hours, guests who never return.',
    },
  ];

  const steps = [
    { icon: Layers, t: isEs ? 'Conectamos' : 'Connect', b: isEs ? 'POS, delivery, Google, reservas y pagos — unificados sin que muevas un dedo.' : 'POS, delivery, Google, reservations and payments — unified without you lifting a finger.' },
    { icon: TrendingUp, t: isEs ? 'Analizamos' : 'Analyze', b: isEs ? 'Los datos se limpian y cruzan cada noche. Un solo número confiable por cada pregunta.' : 'Data is cleaned and crossed nightly. One trustworthy number for every question.' },
    { icon: Zap, t: isEs ? 'Actuamos' : 'Act', b: isEs ? 'Cada semana recibes qué cambiar: precios, menú, horarios, promos. Nosotros lo implementamos.' : 'Every week you get what to change: pricing, menu, hours, promos. We implement it.' },
  ];

  return (
    <div className="bg-white">
      <SEO
        title={isEs ? 'Datos y Analítica para Restaurantes | Kitxens' : 'Restaurant Data & Analytics | Kitxens'}
        description={isEs ? 'Todos los datos de tu restaurante en una sola fuente de verdad. Dashboards en vivo, reportes ejecutivos y business intelligence gestionados por KITXENS.' : 'All your restaurant data in one source of truth. Live dashboards, executive reporting and business intelligence, managed by KITXENS.'}
        path="/services/data-analytics"
      />

      {/* HERO — dark, Snowflake-style */}
      <section className="relative bg-[#0B0D12] text-white pt-28 pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-[#F77F11]/[0.12] blur-3xl" />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">DATA & ANALYTICS</p>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]">
                {isEs ? 'Todos los datos de tu restaurante.' : 'All your restaurant data.'}
                <span className="block mt-3 bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
                  {isEs ? 'Una sola fuente de verdad.' : 'One source of truth.'}
                </span>
              </h1>
              <p className="mt-6 text-neutral-300 text-lg leading-relaxed max-w-xl">
                {isEs
                  ? 'Tu POS dice una cosa, el delivery otra y la contabilidad una tercera. KITXENS unifica cada plato, pedido y turno en un solo panel claro — y te dice qué hacer con él.'
                  : 'Your POS says one thing, delivery another and accounting a third. KITXENS unifies every plate, order and shift into one clear picture — and tells you what to do with it.'}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact?type=discovery" className="inline-flex items-center gap-2 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-12 font-semibold text-[15px] transition-colors" data-testid="da-hero-cta">
                  {isEs ? 'Agendar Discovery' : 'Book a Discovery Call'} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-[#F77F11] hover:text-[#FFB672] px-6 h-12 font-semibold text-[15px] transition-colors" data-testid="da-pricing-cta">
                  {isEs ? 'Desde $99/mes' : 'From $99/mo'}
                </Link>
              </div>
            </div>
            <LiveChart isEs={isEs} />
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'QUÉ INCLUYE' : 'WHAT\'S INSIDE'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'De datos dispersos a decisiones seguras.' : 'From scattered data to confident decisions.'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {capabilities.map(({ icon: Icon, href, t, b }) => (
              <Link key={href} to={href} className="group flex flex-col rounded-3xl border border-neutral-200 bg-white p-7 hover:border-[#F77F11]/60 hover:shadow-[0_30px_70px_-40px_rgba(247,127,17,0.4)] transition-all" data-testid={`da-cap-${href.split('/').pop()}`}>
                <span className="w-11 h-11 rounded-xl bg-neutral-950 text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                <h3 className="mt-5 text-[19px] font-bold text-neutral-900">{t}</h3>
                <p className="mt-2 text-[14px] text-neutral-600 leading-relaxed flex-1">{b}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[13.5px] font-semibold text-[#F77F11]">
                  {isEs ? 'Explorar' : 'Explore'} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section className="py-16 lg:py-20 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? 'CÓMO FUNCIONA' : 'HOW IT WORKS'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Tú diriges el restaurante. Nosotros, los datos.' : 'You run the restaurant. We run the data.'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {steps.map(({ icon: Icon, t, b }, i) => (
              <div key={t} className="rounded-2xl border border-neutral-200 bg-white p-6">
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center"><Icon className="w-5 h-5 text-[#F77F11]" /></span>
                  <span className="text-[26px] font-bold text-neutral-100 select-none">{`0${i + 1}`}</span>
                </div>
                <h3 className="mt-5 font-bold text-neutral-900 text-[16px]">{t}</h3>
                <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[13px] text-neutral-500">
            {isEs
              ? 'Sin hojas de cálculo. Sin exportar CSVs. Sin contratar un analista.'
              : 'No spreadsheets. No CSV exports. No hiring an analyst.'}
          </p>
        </div>
      </section>

      <ContinueExploring context="data-analytics" />
    </div>
  );
}
